import { PlanningViewModel } from "../ViewModels";
import { usePlanningEnricher } from "../Hooks";
import {
    ActionResponse,
    ActionStatusHelper,
    DomainDetailHook,
    useAppEnum,
    useDetailLifeCycle,
    useExportAction,
} from "@/Shared";

import { DOMAIN_CONFIGS, DomainHookProps } from "@/Shared/Config/domain-config";
import { usePlanning } from "@/OneLiners";
import { PlanningModel } from "../Models/PlanningModel";



/**
 * usePlanningDetail hook
 *
 * Domain-specific detail hook for Planning entities.
 * Acts as the orchestration layer between:
 * - domain data access and enrichment,
 * - UI feedback mechanisms (toast notifications, confirmation dialogs),
 * - and navigation concerns.
 *
 * Responsibilities:
 * - Initialize the PlanningViewModel when data is available.
 * - Handle delete operations with access control, confirmation modal, and toast feedback.
 * - Manage navigation (form, detail view, layout) based on user actions and permissions.
 * - Expose entity VM, delete handler, access rights, enums, and navigation methods to the UI layer.
 *
 * @date: March 2, 2026
 * @author: Samano CASTRE
 * 
 * @see DomainHookProps
 * @see DomainDetailHook<PlanningViewModel>
 *
 * @param props DomainHookProps
 * @returns DomainDetailHook<PlanningViewModel>
 */
export function usePlanningDetail(props: DomainHookProps): DomainDetailHook<PlanningViewModel> {
    const { permissionHook, accessRight, uiFeedbackHooks } = props;
    const { domainType, domainLabel } = DOMAIN_CONFIGS.Planning;


    /**
     * Planning domain CRUD operations and state
     */
    const planningHook = usePlanning();

    /**
     * Planning-related enums exposed to the UI layer
     */
    const { PlanningEnums } = useAppEnum();

    /**
     * Enrichment layer
     *
     * Responsible for resolving user-related or dependent data and
     * computing derived Planning fields for the detail view.
     */
    const {
        enricherResponse,
        getStrictEnrichedPlanning,
        getEnrichedPlanningById
    } = usePlanningEnricher();

    /**
     * Combined response acting as a single readiness contract
     * for initializing the detail view
     */
    const response: ActionResponse = {
        status: ActionStatusHelper.combine(
            planningHook.stateResponse.status,
            enricherResponse.status,
            permissionHook.stateResponse.status
        ),
        error: planningHook.stateResponse.error ??
            enricherResponse.error
    };

    /**
      * Detail life cycle hook
      *
      * Handles:
      * - Entity resolution (queryId / entityId)
      * - ViewModel construction
      * - Post-fetch or post-update handling
      * - Provides public API to the UI layer
      */
    const lifeCycleHook = useDetailLifeCycle({
        response,
        domainType,
        props,
        entityHook: planningHook,
        buildVM: (currentEntity: PlanningModel, isDisplaying: boolean) => {
            return new PlanningViewModel({
                planning: getStrictEnrichedPlanning(currentEntity),
                viewEnums: PlanningEnums,
                accessRight,
                isDisplaying
            })
        },
        /**
         * Optional post-success callback
         *
         * Closes the modal if the detail view is inside a modal context.
         */
        onSuccess: uiFeedbackHooks.inModalHook
            ? async () => uiFeedbackHooks.inModalHook?.closeModal()
            : undefined
    });

    const {entityVM} = lifeCycleHook;

    const {computeUIActions} = useExportAction<PlanningModel, PlanningViewModel>({
            accessRight : props.accessRight,
            uiFeedbackHooks: props.uiFeedbackHooks,
            entityVM : entityVM,
            domainType,
            domainLabel,
            entityHook: planningHook
        });
    

    /**
    * Public API exposed to the UI layer
    *
    * Includes ViewModel, delete handlers, access rights, enums, and life cycle methods.
    */
    return {
        ...lifeCycleHook,
        ...computeUIActions
    }
}